//client/src/pages/admin/AdminAnalytics.js
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  BarChart, Bar,
} from 'recharts';
import adminService from '../../services/adminService.js';
import Pagination from '../../components/ui/Pagination.js';

// ─── Chart Colors ────────────────────────────────────────────
const COLORS = {
  gold: '#D4AF37',
  blue: '#3B82F6',
  green: '#10B981',
  purple: '#8B5CF6',
  pink: '#EC4899',
  cyan: '#06B6D4',
  orange: '#F97316',
  red: '#EF4444',
};

const DEVICE_COLORS = {
  Desktop: COLORS.blue,
  Mobile: COLORS.green,
  Tablet: COLORS.purple,
  Unknown: '#6B7280',
};

// ─── Stat Card Component ─────────────────────────────────────
function StatCard({ label, value, subtext }) {
  return (
    <div className="bg-royal-800/40 border border-royal-600 rounded-xl p-5 text-center">
      <p className="text-4xl font-bold text-gold">{value}</p>
      <p className="text-base text-royal-200 mt-1">{label}</p>
      {subtext && <p className="text-sm text-royal-200 mt-1">{subtext}</p>}
    </div>
  );
}

// ─── Chart Card Wrapper ──────────────────────────────────────
function ChartCard({ title, children, className = '' }) {
  return (
    <div className={`bg-royal-800/40 border border-royal-600 rounded-xl p-5 ${className}`}>
      <h2 className="text-white text-lg font-semibold mb-4">{title}</h2>
      {children}
    </div>
  );
}

// ─── Custom Tooltip for Charts ───────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-royal-900 border border-royal-600 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-royal-200 text-sm mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-sm" style={{ color: entry.color }}>
          {entry.name}: <span className="font-semibold">{entry.value.toLocaleString()}</span>
        </p>
      ))}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────
function formatDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatShortDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function EventTypeBadge({ type }) {
  const colors = {
    view: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    send: 'bg-green-500/20 text-green-400 border-green-500/30',
    click: 'bg-gold/20 text-gold border-gold/30',
  };
  return (
    <span className={`px-2 py-1 rounded-full text-sm border ${colors[type] || 'bg-royal-600 text-royal-200 border-royal-500'}`}>
      {type}
    </span>
  );
}

// ─── Period Selector Component ───────────────────────────────
function PeriodSelector({ value, onChange }) {
  const periods = [
    { value: 7, label: '7 days' },
    { value: 30, label: '30 days' },
    { value: 90, label: '90 days' },
  ];
  return (
    <div className="flex gap-2">
      {periods.map((p) => (
        <button
          key={p.value}
          onClick={() => onChange(p.value)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            value === p.value
              ? 'bg-gold/15 text-gold border border-gold/30'
              : 'text-royal-200 border border-royal-600 hover:text-white hover:border-royal-400'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────
export default function AdminAnalytics() {
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState(30);
  const [summaryData, setSummaryData] = useState(null);
  const [userGrowthData, setUserGrowthData] = useState(null);
  const [topSendersData, setTopSendersData] = useState(null);
  const [ctaFunnelData, setCtaFunnelData] = useState(null);
  const [eventsData, setEventsData] = useState({ events: [], total: 0, page: 1, totalPages: 1 });
  const [cardsData, setCardsData] = useState({ cards: [], total: 0, page: 1, totalPages: 1 });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(false);

  // Filters
  const [eventsPage, setEventsPage] = useState(1);
  const [cardsPage, setCardsPage] = useState(1);
  const [filterUserId, setFilterUserId] = useState('');
  const [filterEventType, setFilterEventType] = useState('');

  // Fetch all chart data
  const fetchChartData = useCallback(async (days) => {
    setChartsLoading(true);
    try {
      const [analytics, growth, senders, funnel] = await Promise.all([
        adminService.getAnalytics(days),
        adminService.getUserGrowth(days),
        adminService.getTopSenders(days, 10),
        adminService.getCtaFunnel(days),
      ]);
      setSummaryData(analytics);
      setUserGrowthData(growth);
      setTopSendersData(senders);
      setCtaFunnelData(funnel);
    } catch (err) {
      console.error('Fetch chart data error:', err);
    } finally {
      setChartsLoading(false);
    }
  }, []);

  // Fetch events
  const fetchEvents = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: eventsPage, limit: 25 });
      if (filterUserId) params.set('userid', filterUserId);
      if (filterEventType) params.set('eventtype', filterEventType);
      const data = await adminService.getAnalyticsEvents(params.toString());
      setEventsData(data);
    } catch (err) {
      console.error('Fetch events error:', err);
    }
  }, [eventsPage, filterUserId, filterEventType]);

  // Fetch cards
  const fetchCards = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: cardsPage, limit: 25 });
      const data = await adminService.getAnalyticsCards(params.toString());
      setCardsData(data);
    } catch (err) {
      console.error('Fetch cards error:', err);
    }
  }, [cardsPage]);

  // Fetch users for filter dropdown
  const fetchUsers = useCallback(async () => {
    try {
      const data = await adminService.getUsersList();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Fetch users error:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchChartData(period), fetchUsers()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchChartData, fetchUsers, period]);

  // Refetch when period changes
  useEffect(() => {
    if (!loading) {
      fetchChartData(period);
    }
  }, [period, fetchChartData, loading]);

  // Load events when tab changes or filters change
  useEffect(() => {
    if (activeTab === 'events') {
      fetchEvents();
    }
  }, [activeTab, fetchEvents]);

  // Load cards when tab changes
  useEffect(() => {
    if (activeTab === 'cards') {
      fetchCards();
    }
  }, [activeTab, fetchCards]);

  // Reset page when filters change
  useEffect(() => {
    setEventsPage(1);
  }, [filterUserId, filterEventType]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  // Calculate summary stats
  const eventCounts = {};
  (summaryData?.events || []).forEach((e) => {
    eventCounts[e.eventtype] = parseInt(e.count);
  });

  // Prepare device data for pie chart
  const deviceData = (summaryData?.devices || []).map((d) => ({
    name: d.devicetype || 'Unknown',
    value: parseInt(d.count),
  }));

  // Prepare daily breakdown for area chart
  const dailyData = (summaryData?.dailyBreakdown || []).map((d) => ({
    date: formatShortDate(d.date),
    views: parseInt(d.views) || 0,
    sends: parseInt(d.sends) || 0,
    clicks: parseInt(d.clicks) || 0,
  }));

  // Prepare top templates for bar chart
  const templateData = (summaryData?.topTemplates || []).slice(0, 8).map((t) => ({
    name: t.templatename?.length > 20 ? t.templatename.substring(0, 20) + '...' : t.templatename,
    fullName: t.templatename,
    sent: parseInt(t.send_count) || 0,
    views: parseInt(t.view_count) || 0,
  }));

  // Prepare user growth data with cumulative totals
  const growthData = (() => {
    if (!userGrowthData?.daily) return [];
    let cumulative = userGrowthData.baseCount || 0;
    return userGrowthData.daily.map((d) => {
      cumulative += parseInt(d.signups) || 0;
      return {
        date: formatShortDate(d.date),
        signups: parseInt(d.signups) || 0,
        total: cumulative,
      };
    });
  })();

  // Prepare top senders for bar chart
  const sendersData = (topSendersData?.senders || []).map((s) => ({
    name: `${s.firstname || ''} ${s.lastname || ''}`.trim() || s.email,
    email: s.email,
    sent: parseInt(s.cards_sent) || 0,
    views: parseInt(s.total_views) || 0,
  }));

  // Prepare funnel data
  const funnelData = ctaFunnelData
    ? [
        { name: 'Card Views', value: ctaFunnelData.views, fill: COLORS.blue },
        { name: 'CTA Clicks', value: ctaFunnelData.clicks, fill: COLORS.gold },
        { name: 'Signups', value: ctaFunnelData.conversions, fill: COLORS.green },
      ]
    : [];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Dashboard Back Link */}
      <div className="mb-5">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Dashboard
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="font-display text-2xl text-gold">Platform Analytics</h1>
        {activeTab === 'overview' && (
          <PeriodSelector value={period} onChange={setPeriod} />
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { key: 'overview', label: 'Overview' },
          { key: 'events', label: 'Events' },
          { key: 'cards', label: 'Card Performance' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-lg text-base font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-gold/15 text-gold border border-gold/30'
                : 'text-royal-200 border border-royal-600 hover:text-white hover:border-royal-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
        <a
          href="/admin/cta-results"
          className="px-4 py-2 rounded-lg text-base font-medium transition-colors text-royal-200 border border-royal-600 hover:text-white hover:border-royal-200"
        >
          CTA Results
        </a>
      </div>

      {/* Loading overlay for charts */}
      {chartsLoading && (
        <div className="fixed inset-0 bg-royal-900/50 flex items-center justify-center z-50">
          <div className="w-8 h-8 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label={`Views (${period}d)`} value={(eventCounts.view || 0).toLocaleString()} />
            <StatCard label={`Sends (${period}d)`} value={(eventCounts.send || 0).toLocaleString()} />
            <StatCard label={`CTA Clicks (${period}d)`} value={(eventCounts.click || 0).toLocaleString()} />
            <StatCard label="Total Users" value={(summaryData?.users?.total || 0).toLocaleString()} />
          </div>

          {/* Row 1: Activity Over Time + Device Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Activity Over Time - Takes 2 columns */}
            <ChartCard title="Activity Over Time" className="lg:col-span-2">
              {dailyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={dailyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.blue} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={COLORS.blue} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorSends" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.green} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={COLORS.green} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.gold} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={COLORS.gold} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#9CA3AF" 
                      tick={{ fill: '#9CA3AF', fontSize: 12 }}
                      tickLine={false}
                    />
                    <YAxis 
                      stroke="#9CA3AF" 
                      tick={{ fill: '#9CA3AF', fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="views"
                      name="Views"
                      stroke={COLORS.blue}
                      fillOpacity={1}
                      fill="url(#colorViews)"
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="sends"
                      name="Sends"
                      stroke={COLORS.green}
                      fillOpacity={1}
                      fill="url(#colorSends)"
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="clicks"
                      name="Clicks"
                      stroke={COLORS.gold}
                      fillOpacity={1}
                      fill="url(#colorClicks)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-royal-200 text-center py-10">No activity data available</p>
              )}
              {/* Legend */}
              <div className="flex justify-center gap-6 mt-2">
                <span className="flex items-center gap-2 text-sm text-royal-200">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.blue }} /> Views
                </span>
                <span className="flex items-center gap-2 text-sm text-royal-200">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.green }} /> Sends
                </span>
                <span className="flex items-center gap-2 text-sm text-royal-200">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.gold }} /> Clicks
                </span>
              </div>
            </ChartCard>

            {/* Device Breakdown - Takes 1 column */}
            <ChartCard title="Device Breakdown">
              {deviceData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={deviceData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {deviceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={DEVICE_COLORS[entry.name] || '#6B7280'} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1e1b4b',
                          border: '1px solid #4c1d95',
                          borderRadius: '8px',
                        }}
                        itemStyle={{ color: '#e5e7eb' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Custom Legend */}
                  <div className="flex flex-col gap-2 mt-2">
                    {deviceData.map((entry, i) => {
                      const total = deviceData.reduce((sum, d) => sum + d.value, 0);
                      const pct = total > 0 ? Math.round((entry.value / total) * 100) : 0;
                      return (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2 text-royal-200">
                            <span
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: DEVICE_COLORS[entry.name] || '#6B7280' }}
                            />
                            {entry.name}
                          </span>
                          <span className="text-white font-medium">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <p className="text-royal-200 text-center py-10">No device data available</p>
              )}
            </ChartCard>
          </div>

          {/* Row 2: Top Templates + CTA Funnel */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Templates */}
            <ChartCard title="Top Templates">
              {templateData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={templateData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={true} vertical={false} />
                    <XAxis type="number" stroke="#9CA3AF" tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      stroke="#9CA3AF"
                      tick={{ fill: '#9CA3AF', fontSize: 12 }}
                      width={120}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const data = payload[0].payload;
                        return (
                          <div className="bg-royal-900 border border-royal-600 rounded-lg px-3 py-2 shadow-lg">
                            <p className="text-white text-sm font-medium mb-1">{data.fullName}</p>
                            <p className="text-sm text-royal-200">Sent: <span className="text-gold font-semibold">{data.sent}</span></p>
                            <p className="text-sm text-royal-200">Views: <span className="text-blue-400 font-semibold">{data.views}</span></p>
                          </div>
                        );
                      }}
                    />
                    <Bar dataKey="sent" name="Sent" fill={COLORS.gold} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-royal-200 text-center py-10">No template data available</p>
              )}
            </ChartCard>

            {/* CTA Funnel */}
            <ChartCard title="CTA Conversion Funnel">
              {funnelData.length > 0 && funnelData.some((d) => d.value > 0) ? (
                <>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={funnelData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                      <XAxis dataKey="name" stroke="#9CA3AF" tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                      <YAxis stroke="#9CA3AF" tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1e1b4b',
                          border: '1px solid #4c1d95',
                          borderRadius: '8px',
                        }}
                        itemStyle={{ color: '#e5e7eb' }}
                      />
                      <Bar dataKey="value" name="Count" radius={[4, 4, 0, 0]}>
                        {funnelData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  {/* Conversion rates */}
                  <div className="flex justify-center gap-8 mt-4 text-sm">
                    <div className="text-center">
                      <p className="text-royal-200">Click Rate</p>
                      <p className="text-gold font-bold text-lg">
                        {ctaFunnelData.views > 0
                          ? Math.round((ctaFunnelData.clicks / ctaFunnelData.views) * 100)
                          : 0}%
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-royal-200">Signup Rate</p>
                      <p className="text-green-400 font-bold text-lg">
                        {ctaFunnelData.clicks > 0
                          ? Math.round((ctaFunnelData.conversions / ctaFunnelData.clicks) * 100)
                          : 0}%
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-royal-200 text-center py-10">No funnel data available</p>
              )}
            </ChartCard>
          </div>

          {/* Row 3: User Growth + Top Senders */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* User Growth */}
            <ChartCard title="User Growth">
              {growthData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={growthData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorSignups" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.purple} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={COLORS.purple} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis
                        dataKey="date"
                        stroke="#9CA3AF"
                        tick={{ fill: '#9CA3AF', fontSize: 12 }}
                        tickLine={false}
                      />
                      <YAxis
                        stroke="#9CA3AF"
                        tick={{ fill: '#9CA3AF', fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          return (
                            <div className="bg-royal-900 border border-royal-600 rounded-lg px-3 py-2 shadow-lg">
                              <p className="text-royal-200 text-sm mb-1">{label}</p>
                              <p className="text-sm text-purple-400">
                                New: <span className="font-semibold">{payload[0]?.payload?.signups}</span>
                              </p>
                              <p className="text-sm text-white">
                                Total: <span className="font-semibold">{payload[0]?.value?.toLocaleString()}</span>
                              </p>
                            </div>
                          );
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="total"
                        name="Total Users"
                        stroke={COLORS.purple}
                        fillOpacity={1}
                        fill="url(#colorSignups)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                  <div className="flex justify-center gap-2 mt-2">
                    <span className="flex items-center gap-2 text-sm text-royal-200">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.purple }} /> Cumulative Users
                    </span>
                  </div>
                </>
              ) : (
                <p className="text-royal-200 text-center py-10">No growth data available</p>
              )}
            </ChartCard>

            {/* Top Senders */}
            <ChartCard title="Top Senders">
              {sendersData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={sendersData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={true} vertical={false} />
                    <XAxis type="number" stroke="#9CA3AF" tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      stroke="#9CA3AF"
                      tick={{ fill: '#9CA3AF', fontSize: 12 }}
                      width={100}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const data = payload[0].payload;
                        return (
                          <div className="bg-royal-900 border border-royal-600 rounded-lg px-3 py-2 shadow-lg">
                            <p className="text-white text-sm font-medium mb-1">{data.name}</p>
                            <p className="text-xs text-royal-300 mb-1">{data.email}</p>
                            <p className="text-sm text-royal-200">Cards Sent: <span className="text-pink-400 font-semibold">{data.sent}</span></p>
                            <p className="text-sm text-royal-200">Total Views: <span className="text-blue-400 font-semibold">{data.views}</span></p>
                          </div>
                        );
                      }}
                    />
                    <Bar dataKey="sent" name="Cards Sent" fill={COLORS.pink} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-royal-200 text-center py-10">No sender data available</p>
              )}
            </ChartCard>
          </div>

          {/* Admin Link Performance Table */}
          {summaryData?.adminLinks?.length > 0 && (
            <ChartCard title="Admin Link Performance">
              <div className="overflow-x-auto">
                <table className="w-full text-base">
                  <thead>
                    <tr className="border-b border-royal-700">
                      <th className="py-3 text-left text-royal-200 font-medium">Link Name</th>
                      <th className="py-3 text-left text-royal-200 font-medium">Shortcode</th>
                      <th className="py-3 text-right text-royal-200 font-medium">Clicks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryData.adminLinks.map((link, i) => (
                      <tr key={i} className="border-b border-royal-800">
                        <td className="py-3 text-royal-100">{link.name}</td>
                        <td className="py-3 text-royal-200">{link.shortcode || '—'}</td>
                        <td className="py-3 text-right text-gold font-semibold">{link.clickcount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          )}
        </div>
      )}

      {/* Events Tab */}
      {activeTab === 'events' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <select
              value={filterUserId}
              onChange={(e) => setFilterUserId(e.target.value)}
              className="px-3 py-2.5 bg-royal-900/60 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50"
            >
              <option value="">All Users</option>
              {users.map((u) => (
                <option key={u.userid} value={u.userid}>
                  {u.email} ({u.firstname} {u.lastname})
                </option>
              ))}
            </select>

            <select
              value={filterEventType}
              onChange={(e) => setFilterEventType(e.target.value)}
              className="px-3 py-2.5 bg-royal-900/60 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50"
            >
              <option value="">All Event Types</option>
              <option value="view">Views</option>
              <option value="send">Sends</option>
              <option value="click">Clicks</option>
            </select>

            {(filterUserId || filterEventType) && (
              <button
                onClick={() => { setFilterUserId(''); setFilterEventType(''); }}
                className="px-3 py-2.5 text-royal-200 hover:text-white text-base"
              >
                Clear Filters
              </button>
            )}

            <span className="text-royal-200 text-sm ml-auto">
              {eventsData.total} event{eventsData.total !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Events Table */}
          <div className="bg-royal-800/40 border border-royal-600 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-base">
                <thead>
                  <tr className="border-b border-royal-700 bg-royal-900/40">
                    <th className="px-4 py-3 text-left text-royal-200 font-medium">Date</th>
                    <th className="px-4 py-3 text-left text-royal-200 font-medium">Type</th>
                    <th className="px-4 py-3 text-left text-royal-200 font-medium">User</th>
                    <th className="px-4 py-3 text-left text-royal-200 font-medium">Card</th>
                    <th className="px-4 py-3 text-left text-royal-200 font-medium">Device</th>
                  </tr>
                </thead>
                <tbody>
                  {eventsData.events.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-royal-200">
                        No events found
                      </td>
                    </tr>
                  ) : (
                    eventsData.events.map((event) => (
                      <tr key={event.eventid} className="border-b border-royal-800 hover:bg-royal-800/30">
                        <td className="px-4 py-3 text-royal-100 whitespace-nowrap">
                          {formatDate(event.createdat)}
                        </td>
                        <td className="px-4 py-3">
                          <EventTypeBadge type={event.eventtype} />
                          {event.linktype === 'admin' && (
                            <span className="ml-2 text-sm text-royal-200">(CTA)</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-royal-100">
                          {event.useremail ? (
                            <span title={event.useremail}>
                              {event.userfirstname} {event.userlastname}
                            </span>
                          ) : (
                            <span className="text-royal-200">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-royal-100">
                          {event.moonbeamuuid ? (
                            <span title={event.moonbeamuuid}>
                              {event.templatename || 'Unknown'}
                            </span>
                          ) : (
                            <span className="text-royal-200">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-royal-200">
                          {event.devicetype || '—'}
                          {event.browser && ` / ${event.browser}`}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination page={eventsData.page} totalPages={eventsData.totalPages} onPageChange={setEventsPage} />
        </div>
      )}

      {/* Cards Tab */}
      {activeTab === 'cards' && (
        <div className="space-y-4">
          <p className="text-royal-200 text-base">
            Per-card performance showing views and "Create Your Own" CTA clicks.
          </p>

          {/* Cards Table */}
          <div className="bg-royal-800/40 border border-royal-600 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-base">
                <thead>
                  <tr className="border-b border-royal-700 bg-royal-900/40">
                    <th className="px-4 py-3 text-left text-royal-200 font-medium">Sent</th>
                    <th className="px-4 py-3 text-left text-royal-200 font-medium">Template</th>
                    <th className="px-4 py-3 text-left text-royal-200 font-medium">Sender</th>
                    <th className="px-4 py-3 text-left text-royal-200 font-medium">Recipient</th>
                    <th className="px-4 py-3 text-right text-royal-200 font-medium">Views</th>
                    <th className="px-4 py-3 text-right text-royal-200 font-medium">CTA Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {cardsData.cards.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-royal-200">
                        No cards found
                      </td>
                    </tr>
                  ) : (
                    cardsData.cards.map((card) => (
                      <tr key={card.moonbeamid} className="border-b border-royal-800 hover:bg-royal-800/30">
                        <td className="px-4 py-3 text-royal-100 whitespace-nowrap">
                          {formatDate(card.sentat)}
                        </td>
                        <td className="px-4 py-3 text-royal-100">
                          {card.templatename}
                        </td>
                        <td className="px-4 py-3 text-royal-100">
                          <span title={card.senderemail}>
                            {card.senderfirstname} {card.senderlastname}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-royal-100">
                          {card.recipientname || '—'}
                        </td>
                        <td className="px-4 py-3 text-right text-gold font-semibold">
                          {card.views || 0}
                        </td>
                        <td className="px-4 py-3 text-right text-gold font-semibold">
                          {card.cta_clicks || 0}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination page={cardsData.page} totalPages={cardsData.totalPages} onPageChange={setCardsPage} />
        </div>
      )}
    </div>
  );
}